/// <amd-module name="VSSUI/Components/TreeEx/FixedHeightTree" />
import * as React from "react";

import { FixedHeightList, IFixedHeightListItemDetails, IFixedHeightListMaterializedStats } from "VSSUI/List";
import { UncheckedObserver } from "VSSUI/Observer";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { FixedHeightTreeRowRenderer, IFixedHeightTree, IFixedHeightTreeProps, IFixedHeightTreeRowDetails } from "./FixedHeightTree.Props";
import { ITreeRow } from "./Tree.Props";

export class FixedHeightTree<T> extends React.Component<IFixedHeightTreeProps<T>> implements IFixedHeightTree<T> {
    private list = React.createRef<FixedHeightList<ITreeItemEx<T>>>();

    public render(): JSX.Element {
        const { role = "tree" } = this.props;

        return (
            <FixedHeightList
                className={this.props.className}
                eventDispatch={this.props.eventDispatch}
                focuszoneProps={this.props.focuszoneProps}
                id={this.props.id}
                itemProvider={this.props.itemProvider}
                maxHeight={this.props.maxHeight}
                onActivate={this.onActivateExpand}
                onFocus={this.props.onFocus}
                onSelect={this.props.onSelect}
                pageSize={this.props.pageSize}
                renderRow={this.renderRow}
                role={role}
                rowHeight={this.props.rowHeight}
                ref={this.list}
                selection={this.props.selection}
                singleClickActivation={this.props.singleClickActivation || true}
                width={this.props.width || "100%"}
            />
        );
    }

    public getFocusIndex(): number {
        if (this.list.current) {
            return this.list.current.getFocusIndex();
        }

        return -1;
    }

    public getStats(): IFixedHeightListMaterializedStats {
        if (this.list.current) {
            return this.list.current.getStats();
        }

        return {
            firstMaterialized: -1,
            lastMaterialized: -1
        };
    }

    public scrollIntoView(rowIndex: number, options?: ScrollIntoViewOptions): void {
        if (this.list.current) {
            return this.list.current.scrollIntoView(rowIndex, options);
        }
    }
    private onActivateExpand = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITreeRow<T>): void => {
        if (!event.defaultPrevented && tableRow.data.underlyingItem.childItems) {
            this.props.onToggle && this.props.onToggle(event, tableRow.data);
            event.preventDefault();
        }
    };

    private renderRow = (rowIndex: number, item: ITreeItemEx<T>, details: IFixedHeightListItemDetails<ITreeItemEx<T>>): JSX.Element => {
        if (!this.props.renderRow || !details.data) {
            return <div />;
        }
        const rowDetails: IFixedHeightTreeRowDetails<T> = {
            ariaRowOffset: details.ariaRowOffset,
            eventDispatch: details.eventDispatch,
            data: details.data,
            itemProvider: this.props.itemProvider,
            listProps: details.listProps,
            onFocusItem: details.onFocusItem
        };
        return (
            <UncheckedObserver data={item.underlyingItem.data}>
                {(props: { data?: T }) => {
                    if (props.data) {
                        // We need to forward the onToggle handler to the treeItemEx before it is rendered.
                        item.onToggle = item.underlyingItem.childItems ? this.props.onToggle : undefined;

                        // First determine if the item supplied a custom row rendering function, if not
                        // attempt to use the global row rendering function.
                        const renderRow: FixedHeightTreeRowRenderer<T> = (item.underlyingItem.data as any).renderRow || this.props.renderRow;

                        return renderRow(rowIndex, item, rowDetails);
                    } else {
                        const { renderLoadingRow } = this.props;

                        // If a custom row loading animation is available use it.
                        if (renderLoadingRow) {
                            return renderLoadingRow(rowIndex, rowDetails);
                        }

                        // Return the default row loading animation.
                        return (
                            <div className="bolt-list-row-loading">
                                <div className="shimmer shimmer-line" style={{ width: Math.random() * 80 + 20 + "%" }}>
                                    &nbsp;
                                </div>
                            </div>
                        );
                    }
                }}
            </UncheckedObserver>
        );
    };
}
